import { Autocomplete } from 'src/components/Autocomplete';
import { ListItem } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { TextField, TextFieldProps } from 'src/components/TextField';

type AutocompleteOption = {
  id: string | number;
  name: string;
};

interface SelectFilterConfig {
  field: string;
  label: string;
  value: any;
  setValue: (newVal: any, close?: boolean) => void;
  showDefaultOption?: boolean;
  defaultOptionLabel?: string;
  options?: TextFieldProps['options'];
  valueOnly?: boolean;
  showList?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  onChange?: (newVal: any) => void;
  multiple?: boolean;
}

export const SelectFilter: React.FC<SelectFilterConfig> = ({
  label,
  showDefaultOption = true,
  defaultOptionLabel = 'Any',
  options,
  value,
  setValue,
  valueOnly,
  showList,
  searchable,
  disabled,
  onChange,
  multiple,
}) => {
  if (valueOnly) {
    if (multiple) {
      const valuesArray: any[] = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
      const selectedOptions = options?.filter((opt) =>
        valuesArray.some((item) => String(item) === String(opt.value))
      );
      if (!selectedOptions || selectedOptions.length === 0) {
        return '';
      }
      return selectedOptions.map((opt) => opt.label).join(', ');
    }

    const selectedOption = options?.find(
      (opt) => String(opt.value) === String(value)
    );
    return selectedOption?.label || value;
  }

  if (!options || !options.length) {
    return null;
  }

  if (searchable && !multiple) {
    const autocompleteOptions: AutocompleteOption[] = [
      ...(showDefaultOption ? [{ id: '', name: defaultOptionLabel }] : []),
      ...options.map((option) => ({
        id: option.value ?? '',
        name: String(option.label),
      })),
    ];

    const selectedOption =
      autocompleteOptions.find(
        (option) => String(option.id) === String(value ?? '')
      ) ?? null;

    return (
      <Autocomplete
        size="small"
        disabled={disabled}
        options={autocompleteOptions}
        value={selectedOption}
        onChange={(_, newValue) => {
          const nextValue = newValue?.id ?? '';
          setValue(nextValue, true);
          onChange?.(nextValue);
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField {...params} label={label} size="small" />
        )}
      />
    );
  }

  return (
    <>
      <TextField
        label={label}
        onChange={(e) => {
          const newValue = e.target.value;

          if (multiple) {
            const nextValues = Array.isArray(newValue)
              ? newValue
              : newValue != null
                ? [newValue]
                : [];
            setValue(nextValues, false);
            onChange && onChange?.(nextValues);
          } else {
            setValue(newValue, true);
            onChange && onChange?.(newValue);
          }
        }}
        value={
          multiple
            ? Array.isArray(value)
              ? value
              : value != null
                ? [value]
                : []
            : (value ?? '')
        }
        options={[
          ...(multiple
            ? []
            : showDefaultOption
              ? [{ value: '', label: defaultOptionLabel }]
              : []),
          ...options,
        ]}
        disabled={disabled}
        slotProps={{
          select: {
            multiple,
          },
        }}
      />
      {showList && !disabled && (
        <Scroller direction="column" sx={{ maxHeight: '200px', mt: 1 }}>
          <Stack spacing={1}>
            {options.map((option) => {
              const currentValues = multiple
                ? Array.isArray(value)
                  ? value
                  : value != null
                    ? [value]
                    : []
                : value;

              const isSelected = multiple
                ? Array.isArray(currentValues) &&
                  currentValues.includes(option.value)
                : currentValues === option.value;

              return (
                <ListItem
                  key={option.value}
                  clickable
                  rounded
                  onClick={() => {
                    if (multiple) {
                      const valuesArray = Array.isArray(currentValues)
                        ? currentValues
                        : currentValues != null
                          ? [currentValues]
                          : [];

                      const exists = valuesArray.includes(option.value);
                      const nextValues = exists
                        ? valuesArray.filter((v) => v !== option.value)
                        : [...valuesArray, option.value];

                      setValue(nextValues, false);
                      onChange && onChange?.(nextValues);
                    } else {
                      setValue(option.value);
                      onChange && onChange?.(option.value);
                    }
                  }}
                  selected={isSelected}
                  primary={option.label}
                ></ListItem>
              );
            })}
          </Stack>
        </Scroller>
      )}
    </>
  );
};
